/**
 * Claspora panel menu.
 * Desktop: text tabs on the header; a tab opens a full-width panel under the header. Hover opens a tab's pane and a click
 * goes to the tab's page (touch / keyboard: first press opens, second goes);
 * leaving the menu, Escape or a click outside closes it.
 * Phones: the menu button opens the phone sheet (.cl-sheet: quiz, band rows, Claspie chips; "sheet mode", set by
 * CSS showing the toggle); the close button, Escape or a tap on the backdrop closes it.
 * Arrow keys move between tabs (WAI-ARIA tabs pattern).
 */
class ClaspLabPanelMenu extends HTMLElement {
  /** @type {number | undefined} */
  #closeTimer;

  connectedCallback() {
    this.tabs = /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('button[role="tab"]')));
    this.body = /** @type {HTMLElement | null} */ (this.querySelector('.cl-panel__body'));
    this.toggle = /** @type {HTMLButtonElement | null} */ (this.querySelector('.cl-panel__toggle'));
    if (!this.body || this.tabs.length === 0) return;

    this.toggle?.addEventListener('click', this.#onToggle);
    this.querySelector('.cl-panel__close')?.addEventListener('click', () => this.close());

    for (const tab of this.tabs) {
      tab.addEventListener('click', this.#onTabClick);
      tab.addEventListener('pointerdown', (event) => (this.#lastPointer = event.pointerType));
      tab.addEventListener('pointerenter', this.#onTabHover);
      tab.addEventListener('keydown', this.#onTabKeydown);
    }
    // Product rows glide sideways on mouse hover to show the rest (Poubel's pattern)
    for (const group of this.querySelectorAll('.cl-panel__group')) {
      const row = /** @type {HTMLElement | null} */ (group.querySelector('.cl-panel__thumbs'));
      if (!row) continue;
      group.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.#glide(row, row.scrollWidth - row.clientWidth, 45);
      });
      group.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.#glide(row, 0, 400);
      });
    }
    this.addEventListener('pointerenter', this.#cancelClose);
    this.addEventListener('pointerleave', this.#scheduleClose);
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
    window.clearTimeout(this.#closeTimer);
    this.#lockScroll(false);
  }

  /** True when the phone sheet is in use (CSS shows the menu button only then). */
  get isSheet() {
    return Boolean(this.toggle && getComputedStyle(this.toggle).display !== 'none');
  }

  /** @param {boolean} lock */
  #lockScroll(lock) {
    document.documentElement.classList.toggle('cl-panel-locked', lock);
  }

  #onToggle = () => {
    if (this.isOpen) {
      this.close();
      return;
    }
    const selected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    if (selected) this.open(selected);
    selected?.focus({ preventScroll: true });
  };

  get isOpen() {
    return this.hasAttribute('open');
  }

  /** @param {HTMLButtonElement} tab */
  open(tab) {
    window.clearTimeout(this.#closeTimer);
    for (const other of this.tabs) {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;
      const pane = document.getElementById(other.getAttribute('aria-controls') || '');
      if (pane) pane.hidden = !selected;
    }
    if (this.body) this.body.hidden = false;
    // Desktop: the full-width panel is fixed, so it starts right under the header row wherever that is
    if (!this.isSheet) {
      const row = this.closest('.header__row') || this.closest('#header-component');
      if (row) this.style.setProperty('--cl-menu-top', `${Math.round(row.getBoundingClientRect().bottom)}px`);
    }
    this.setAttribute('open', '');
    this.toggle?.setAttribute('aria-expanded', 'true');
    if (this.isSheet) this.#lockScroll(true);
  }

  close() {
    window.clearTimeout(this.#closeTimer);
    if (!this.isOpen) return;
    const wasSheet = this.isSheet;
    this.removeAttribute('open');
    if (this.body) this.body.hidden = true;
    for (const tab of this.tabs) tab.setAttribute('aria-selected', 'false');
    this.toggle?.setAttribute('aria-expanded', 'false');
    this.#lockScroll(false);
    if (wasSheet) this.toggle?.focus({ preventScroll: true });
  }

  /** @param {MouseEvent} event */
  #onTabClick = (event) => {
    const tab = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const alreadyOpen = this.isOpen && tab.getAttribute('aria-selected') === 'true';
    const isMouse =
      (event instanceof PointerEvent && event.pointerType === 'mouse') || this.#lastPointer === 'mouse';
    // In the phone sheet the tabs only switch panes; the close button closes.
    if (this.isSheet) {
      this.open(tab);
      return;
    }
    // Desktop: a mouse already opened the pane on hover, so its click goes to the tab's page (All bands,
    // All Claspies; Cris 15 Oct). Touch and keyboard: the first tap opens the pane, the second goes to the page.
    if ((isMouse || alreadyOpen) && tab.dataset.href) {
      window.location.href = tab.dataset.href;
      return;
    }
    this.open(tab);
  };

  /** @type {string} */
  #lastPointer = '';

  /** @param {PointerEvent} event */
  #onTabHover = (event) => {
    if (event.pointerType !== 'mouse' || this.isSheet) return;
    this.open(/** @type {HTMLButtonElement} */ (event.currentTarget));
  };

  /** @param {KeyboardEvent} event */
  #onTabKeydown = (event) => {
    const index = this.tabs.indexOf(/** @type {HTMLButtonElement} */ (event.currentTarget));
    let next = -1;
    if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.tabs.length - 1;
    else if (event.key === 'ArrowDown' && this.isOpen) {
      event.preventDefault();
      const pane = document.getElementById(this.tabs[index]?.getAttribute('aria-controls') || '');
      pane?.querySelector('a')?.focus();
      return;
    }
    if (next === -1) return;
    event.preventDefault();
    const tab = this.tabs[next];
    if (!tab) return;
    tab.focus();
    if (this.isOpen) this.open(tab);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    const selected = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    this.close();
    selected?.focus();
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.isOpen || !(event.target instanceof Node)) return;
    // The sheet's backdrop is this element's own ::after, so a tap on it targets the host.
    if (!this.contains(event.target) || event.target === this) this.close();
  };

  /** @type {WeakMap<HTMLElement, number>} */
  #glides = new WeakMap();

  /**
   * Scrolls a row to `target` at `speed` px per second (linear), cancelling any glide in progress.
   * Skipped when the shopper prefers reduced motion.
   * @param {HTMLElement} row
   * @param {number} target
   * @param {number} speed
   */
  #glide(row, target, speed) {
    cancelAnimationFrame(this.#glides.get(row) || 0);
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const start = row.scrollLeft;
    const distance = target - start;
    if (Math.abs(distance) < 1) return;
    const duration = (Math.abs(distance) / speed) * 1000;
    let t0 = 0;
    /** @param {number} now */
    const step = (now) => {
      if (!t0) t0 = now;
      const progress = Math.min(1, (now - t0) / duration);
      row.scrollLeft = start + distance * progress;
      if (progress < 1) this.#glides.set(row, requestAnimationFrame(step));
    };
    this.#glides.set(row, requestAnimationFrame(step));
  }

  #cancelClose = () => window.clearTimeout(this.#closeTimer);

  /** @param {PointerEvent} event */
  #scheduleClose = (event) => {
    if (event.pointerType !== 'mouse' || this.isSheet) return;
    window.clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => this.close(), 200);
  };
}

if (!customElements.get('cl-panel-menu')) customElements.define('cl-panel-menu', ClaspLabPanelMenu);
